import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PawPrint, Syringe, Phone, MapPin, Clipboard, Heart, Pill, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function PublicPassport() {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Boarding view = a focused, glanceable subset for sitters/boarders.
  // Triggered via `?view=boarding` so the same passport URL can switch modes
  // without minting a separate token (one token, multiple presentations).
  const isBoardingView = useMemo(
    () => (searchParams.get("view") || "").toLowerCase() === "boarding",
    [searchParams]
  );

  useEffect(() => {
    fetch(`${API}/passport/public/${shareToken}`)
      .then(r => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setPet)
      .catch(() => setError("This passport link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAF9F6" }}>
        <div className="w-10 h-10 rounded-full border-4 border-[#FF8A65] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAF9F6" }}>
        <div className="text-center">
          <PawPrint size={40} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700 font-['Nunito']">Passport Not Found</h2>
          <p className="text-slate-500 mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const activeMeds = pet?.active_medications || [];

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }} data-testid={isBoardingView ? "public-passport-boarding" : "public-passport-full"}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-2 mb-8">
          <PawPrint size={20} className="text-[#FF8A65]" />
          <span className="font-['Fredoka_One'] text-xl text-slate-800">Pet Atlas</span>
          <span className="text-slate-400 text-sm ml-2">
            · {isBoardingView ? "Sitter / Boarding View" : "Shared Pet Passport"}
          </span>
        </div>

        {/* Boarding-view banner — sets sitter expectations: this is the
            short version, vet records and timeline are intentionally hidden. */}
        {isBoardingView && (
          <div className="mb-6 bg-[#FFF5E6] border border-[#FFD7AE] rounded-2xl px-4 py-3 flex items-start gap-3" data-testid="boarding-banner">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
              <Briefcase size={16} className="text-[#FF8A65]" />
            </div>
            <div>
              <p className="text-sm font-bold text-[#E65100] font-['Nunito']">Boarding-ready snapshot</p>
              <p className="text-xs text-slate-600 mt-0.5">Everything a sitter needs at a glance — meds, allergies, vaccines, who to call.</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-[0_16px_48px_rgba(0,0,0,0.08)] border border-slate-100 overflow-hidden">
          <div className="h-3 bg-gradient-to-r from-[#FF8A65] via-[#FFD54F] to-[#81C784]" />
          <div className="p-8">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                {pet.photo_url ? (
                  <img src={pet.photo_url} alt={pet.name} className="w-24 h-24 rounded-2xl object-cover shadow-md" />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-[#FFF5E6] flex items-center justify-center shadow-md">
                    <PawPrint size={36} className="text-[#FF8A65]" />
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
                  {isBoardingView ? "Sitter Card" : "Pet Atlas Pet Passport"}
                </div>
                <h1 className="text-3xl font-black text-slate-800 font-['Nunito']">{pet.name}</h1>
                <p className="text-slate-500 font-medium capitalize mt-1">{pet.species}{pet.breed ? ` · ${pet.breed}` : ""}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pet.color && <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">{pet.color}</span>}
                  {pet.weight && !isBoardingView && <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">{pet.weight} kg</span>}
                  {pet.microchip_id && <span className="text-xs bg-[#E3F2FD] text-[#1565C0] px-2.5 py-1 rounded-full font-medium">Chip: {pet.microchip_id}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Active medications — boarding-view critical info, surfaced first. */}
          {isBoardingView && activeMeds.length > 0 && (
            <div className="px-8 pb-6" data-testid="boarding-medications">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Current Medications</h3>
              <div className="rounded-2xl overflow-hidden border border-[#FFCDD2] bg-[#FFF8F8]">
                <ul className="divide-y divide-[#FFCDD2]">
                  {activeMeds.map((m, i) => (
                    <li key={`${m.title || "med"}-${m.due_date || i}`} className="flex items-start gap-3 px-4 py-3">
                      <Pill size={15} className="text-[#E53935] mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 text-sm">{m.title || "Medication"}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Next dose: {formatDate(m.due_date)}
                        </p>
                        {m.notes && (
                          <p className="text-xs text-slate-600 mt-1 italic">{m.notes}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {pet.medical_notes && (
            <div className="px-8 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Medical Notes</h3>
              <div className="bg-[#FFFDE7] rounded-2xl p-4 flex items-start gap-3 border border-[#FFF9C4]">
                <Clipboard size={15} className="text-[#F57F17] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">{pet.medical_notes}</p>
              </div>
            </div>
          )}

          {(pet.vet_name || pet.vet_phone) && (
            <div className="px-8 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Veterinarian</h3>
              <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
                {pet.vet_name && (
                  <div className="flex items-center gap-3">
                    <Heart size={14} className="text-[#EF5350]" />
                    <span className="text-sm font-medium text-slate-700">{pet.vet_name}</span>
                  </div>
                )}
                {pet.vet_phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={14} className="text-[#42A5F5]" />
                    <a href={`tel:${pet.vet_phone}`} className="text-sm text-[#1565C0] hover:underline">{pet.vet_phone}</a>
                  </div>
                )}
                {pet.vet_address && !isBoardingView && (
                  <div className="flex items-start gap-3">
                    <MapPin size={14} className="text-[#FF8A65] mt-0.5" />
                    <span className="text-sm text-slate-600">{pet.vet_address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {pet.emergency_contacts?.length > 0 && (
            <div className="px-8 pb-6" data-testid="public-emergency-contacts">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Emergency Contacts</h3>
              <div className="rounded-2xl overflow-hidden border border-slate-100 divide-y divide-slate-100">
                {pet.emergency_contacts.map((c, i) => (
                  <div key={`ec-${c.name || i}-${c.phone || i}`} className="flex items-center justify-between px-4 py-3 bg-white">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{c.name || "—"}</span>
                        {c.relationship && <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{c.relationship}</span>}
                      </div>
                      {c.phone && <a href={`tel:${c.phone}`} className="text-xs text-[#1565C0] hover:underline">{c.phone}</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pet.vaccinations?.length > 0 && (
            <div className="px-8 pb-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3 flex items-center gap-2">
                <Syringe size={12} className="text-slate-400" /> Vaccination Records
              </h3>
              <div className="rounded-2xl overflow-hidden border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Vaccine</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Given</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wide">Next Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pet.vaccinations.map((v, i) => (
                      <tr key={`${v.name || "vacc"}-${v.date || i}`} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                        <td className="px-4 py-3 font-medium text-slate-800">{v.name}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(v.date)}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-[#E8F5E9] text-[#388E3C] px-2 py-0.5 rounded-full font-medium">{formatDate(v.next_due)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          {isBoardingView
            ? "Boarding view · Pet Atlas — full passport at the same link without ?view=boarding"
            : "Shared via Pet Atlas · Pet Life Manager"}
        </p>
      </div>
    </div>
  );
}
